/**
 * Supplier(廠商)schema — REQ-20260520 ERP Phase 1B。
 * 結構參考 customer.schema,但少 ownerId / industryId / paymentCert / paymentMethod。
 */
import { z } from 'zod';

const decimalString = z
  .union([z.string(), z.number()])
  .transform((v) => (typeof v === 'number' ? String(v) : v))
  .refine((s) => /^-?\d+(\.\d+)?$/.test(s.trim()), '金額格式不正確')
  .transform((s) => s.trim());

export const SupplierPhoneInputSchema = z.object({
  phoneType: z.enum(['PHONE', 'MOBILE', 'FAX']),
  /**
   * 槽位 index(per type 0-based);若省略後端用 array index。
   * 前端固定槽位 UI 會明確帶,讓「PHONE 槽 0 空、槽 1 填」這種 partial fill
   * 在重載時還能對回正確槽位。
   */
  displayOrder: z.number().int().nonnegative().optional(),
  number: z.string().trim().min(1).max(30),
  note: z.string().trim().max(100).nullable().optional(),
});
export type SupplierPhoneInput = z.infer<typeof SupplierPhoneInputSchema>;

export const SupplierPhoneViewSchema = SupplierPhoneInputSchema.extend({
  id: z.number().int(),
  displayOrder: z.number().int(),
});
export type SupplierPhoneView = z.infer<typeof SupplierPhoneViewSchema>;

const baseSupplierFields = {
  code: z.string().trim().max(50).nullable().optional(),
  fullName: z.string().trim().min(1, '公司全名必填').max(200),
  simpleName: z.string().trim().min(1, '簡稱必填').max(50),
  tax: z.string().trim().max(20).nullable().optional(),
  /** 稅額(REQ-20260902004,免稅於 REQ-20260902005 加入):含稅 / 稅外加 / 免稅。純記錄欄位,不進任何金額計算 */
  taxMode: z.enum(['EXCLUSIVE', 'INCLUSIVE', 'EXEMPT']).nullable().optional(),
  categoryId: z.coerce.number().int().positive().nullable().optional(),
  boss: z.string().trim().max(50).nullable().optional(),
  contact1: z.string().trim().max(50).nullable().optional(),
  contact2: z.string().trim().max(50).nullable().optional(),
  email: z.string().trim().email().max(320).nullable().optional().or(z.literal('').transform(() => null)),
  website: z.string().trim().max(2083).nullable().optional(),
  address: z.string().trim().max(255).nullable().optional(),
  addressShipping: z.string().trim().max(255).nullable().optional(),
  addressInvoice: z.string().trim().max(255).nullable().optional(),
  paymentTerms: z.string().trim().max(30).nullable().optional(),
  paymentDays: z.coerce.number().int().min(0).max(365).nullable().optional(),
  closingDay: z.coerce.number().int().min(0).max(31).nullable().optional(),
  bankCode: z.string().trim().max(20).nullable().optional(),
  /** 銀行代號(REQ-20260902004)。與 bankCode 是兩個不同的欄位,見 schema.prisma 註解 */
  bankNo: z.string().trim().max(20).nullable().optional(),
  bankAccount: z.string().trim().max(50).nullable().optional(),
  openingPayable: decimalString.nullable().optional(),
  openingPrepaid: decimalString.nullable().optional(),
  notes: z.string().trim().max(5000).nullable().optional(),
  phones: z.array(SupplierPhoneInputSchema).max(20).default([]),
};

export const CreateSupplierSchema = z.object(baseSupplierFields);
export type CreateSupplierInput = z.infer<typeof CreateSupplierSchema>;

export const UpdateSupplierSchema = z.object(baseSupplierFields).partial();
export type UpdateSupplierInput = z.infer<typeof UpdateSupplierSchema>;

export const SupplierViewSchema = z.object({
  id: z.number().int(),
  uuid: z.string(),
  code: z.string().nullable(),
  fullName: z.string(),
  simpleName: z.string(),
  tax: z.string().nullable(),
  taxMode: z.enum(['EXCLUSIVE', 'INCLUSIVE', 'EXEMPT']).nullable(),
  categoryId: z.number().int().nullable(),
  categoryName: z.string().nullable(),
  boss: z.string().nullable(),
  contact1: z.string().nullable(),
  contact2: z.string().nullable(),
  email: z.string().nullable(),
  website: z.string().nullable(),
  address: z.string().nullable(),
  addressShipping: z.string().nullable(),
  addressInvoice: z.string().nullable(),
  paymentTerms: z.string().nullable(),
  paymentDays: z.number().int().nullable(),
  closingDay: z.number().int().nullable(),
  bankCode: z.string().nullable(),
  bankNo: z.string().nullable(),
  bankAccount: z.string().nullable(),
  openingPayable: z.string().nullable(),
  openingPrepaid: z.string().nullable(),
  notes: z.string().nullable(),
  phones: z.array(SupplierPhoneViewSchema),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type SupplierView = z.infer<typeof SupplierViewSchema>;

export const SupplierListQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(25),
  q: z.string().trim().max(100).optional(),
  categoryId: z.coerce.number().int().positive().optional(),
  // `categoryName` 不是 Supplier 上的欄位,是 relation(REQ-20260916003 的搜尋廠商彈窗
  // 要依類別排序);service 的 list() 會把它映射成 `{ category: { name: order } }`。
  sort: z
    .enum(['code', 'simpleName', 'fullName', 'tax', 'categoryName', 'updatedAt', 'createdAt'])
    .default('updatedAt'),
  order: z.enum(['asc', 'desc']).default('desc'),
});
export type SupplierListQuery = z.infer<typeof SupplierListQuerySchema>;

export const SupplierListItemSchema = z.object({
  id: z.number().int(),
  code: z.string().nullable(),
  simpleName: z.string(),
  fullName: z.string(),
  tax: z.string().nullable(),
  categoryName: z.string().nullable(),
  primaryPhone: z.string().nullable(),
  updatedAt: z.string().datetime(),
});
export type SupplierListItem = z.infer<typeof SupplierListItemSchema>;

// REQ-20260522 Phase 1B-Console:廠商操作台 prev/next 翻頁
// ⚠️ 刻意**不**與上面 `SupplierListQuerySchema.sort` 共用:findNeighbor() 會把 sortBy
// 直接塞進 Prisma 的 `select` / `where` / `orderBy` 當平鋪欄位名,`categoryName` 這種
// relation 鍵會讓上一筆 / 下一筆整個壞掉。兩者各自獨立(REQ-20260916003)。
export const SUPPLIER_SORTABLE_FIELDS = ['code', 'simpleName', 'fullName', 'updatedAt', 'createdAt'] as const;
export type SupplierSortableField = (typeof SUPPLIER_SORTABLE_FIELDS)[number];

export const SupplierNeighborQuerySchema = z.object({
  direction: z.enum(['previous', 'next']),
  sortBy: z.enum(SUPPLIER_SORTABLE_FIELDS).default('updatedAt'),
  order: z.enum(['asc', 'desc']).default('desc'),
});
export type SupplierNeighborQuery = z.infer<typeof SupplierNeighborQuerySchema>;
